import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { ProductCatalogCreate, ProductCatalogItem, ProductCatalogUpdate } from '../../utils/productCatalogApi';

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  EQUIPMENT: 'Equipment',
  CONSUMABLE: 'Consumable',
  FOOD: 'Food',
  SUPPLEMENT: 'Supplement',
  ACCESSORY: 'Accessory',
  OTHER: 'Other',
};

interface ProductCatalogItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ProductCatalogCreate | ProductCatalogUpdate) => Promise<void>;
  onRequestDelete?: () => void;
  item: ProductCatalogItem | null;
  mode: 'create' | 'edit';
}

export const ProductCatalogItemModal: React.FC<ProductCatalogItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onRequestDelete,
  item,
  mode,
}) => {
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [isStockTracked, setIsStockTracked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setName(item.name || '');
      setProductType(item.product_type || 'OTHER');
      setDescription(item.description || '');
      setSku(item.sku || '');
      setManufacturer(item.manufacturer || '');
      setIsStockTracked(item.is_stock_tracked ?? false);
    } else {
      setName('');
      setProductType('OTHER');
      setDescription('');
      setSku('');
      setManufacturer('');
      setIsStockTracked(false);
    }
  }, [isOpen, item?.id]);

  if (!isOpen) return null;

  const title =
    mode === 'create'
      ? item?.is_standard
        ? 'Customize Product'
        : 'Add Product'
      : 'Edit Product';

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (mode === 'create') {
        const payload: ProductCatalogCreate = {
          name: name.trim(),
          product_type: productType,
          description: description || undefined,
          sku: sku || undefined,
          manufacturer: manufacturer || undefined,
          is_stock_tracked: isStockTracked,
          ...(item?.is_standard ? { based_on_standard_id: item.id } : {}),
        };
        await onSave(payload);
      } else {
        const payload: ProductCatalogUpdate = {
          name: name.trim(),
          product_type: productType,
          description: description || undefined,
          sku: sku || undefined,
          manufacturer: manufacturer || undefined,
          is_stock_tracked: isStockTracked,
        };
        await onSave(payload);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            >
              {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
            <input
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
            <input
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="product_is_stock_tracked"
              checked={isStockTracked}
              onChange={(e) => setIsStockTracked(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="product_is_stock_tracked" className="text-sm font-medium text-gray-700">
              Track as Inventory (stock-tracked)
            </label>
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <div>
            {mode === 'edit' && item && !item.is_standard && onRequestDelete && (
              <button onClick={onRequestDelete} className="px-4 py-2 text-red-600 hover:text-red-800">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


